import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LiveHelpOutlinedIcon from "@mui/icons-material/LiveHelpOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  SelectedTab,
  selectSelectedTab,
  setSelectedTab,
} from "../../app/slices/GlobalSlice";
import { selectIsDirty } from "../../app/slices/TrainingSlice";
import {
  selectCurrentOrganization,
  selectUsername,
} from "../../app/slices/UserSlice";
import { selectLocalChanges } from "../../app/slices/WebSocketSlice";
import PiConfirmationDialog from "../../components/PiConfirmationDialog";
import { useSaveTraining } from "../../hooks/SaveTrainingHook";
import useOnExitEditor from "../../hooks/OnExitEditorHook";
import EditorRail, {
  EditorRailButton,
  EditorRailDivider,
} from "../editor_rail/EditorRail";
import FileExplorerDialog from "../file_explorer/FileExplorerDialog";
import PiAccountDialog from "../menu/PiAccountPopover";
import PiSwitchOrganizationDialog from "../menu/PiSwitchOrganizationDialog";
import { OnboardingTarget } from "../onboarding/onboardingTargets";
import { useTourLauncher } from "../onboarding/useTourLauncher";
import SettingsModal from "../menu/SettingsModal";
import ProjectSettingsDialog from "../project_settings/ProjectSettingsDialog";
import PublishDialog from "../publish_course/PublishDialog";
import LocalChangesModal from "../send_to_unity/LocalChangesModal";

export default function TrainingEditor360Rail() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedTab = useSelector(selectSelectedTab);
  const isDirty = useSelector(selectIsDirty);
  const localChanges = useSelector(selectLocalChanges);
  const username = useSelector(selectUsername);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const { saveTraining } = useSaveTraining();
  const { onExitEditorCleanup } = useOnExitEditor();
  const {
    label: tourLabel,
    canStart: canStartTour,
    startTour,
  } = useTourLauncher();

  const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(false);
  const [isProjectSettingsOpen, setIsProjectSettingsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [isLocalChangesModalOpen, setIsLocalChangesModalOpen] = useState(false);
  const [confirmLeaveDialogOpen, setConfirmLeaveDialogOpen] = useState(false);
  const [accountAnchorEl, setAccountAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [isSwitchOrganizationOpen, setIsSwitchOrganizationOpen] =
    useState(false);

  const accountTooltip = currentOrganization?.name
    ? `${username} - ${currentOrganization.name}`
    : username;

  const onSaveProject = useCallback(async () => {
    if (Object.keys(localChanges).length === 0) {
      await saveTraining();
    } else {
      setIsLocalChangesModalOpen(true);
    }
  }, [localChanges, saveTraining]);

  function onExitEditor() {
    onExitEditorCleanup();
    navigate("/");
  }

  function onExitClicked() {
    if (isDirty) {
      setConfirmLeaveDialogOpen(true);
    } else {
      onExitEditor();
    }
  }

  return (
    <EditorRail
      testId="trainingeditor-360-rail"
      bottom={
        <>
          <EditorRailButton
            label={t("settings") as string}
            icon={<SettingsOutlinedIcon fontSize="inherit" />}
            onClick={() => setIsSettingsOpen(true)}
            testId="trainingeditor-360-settings"
          />
          {/* Distinct from the "support" button further up, which opens the
              external help site — this one starts the in-app tour. */}
          <EditorRailButton
            label={tourLabel}
            icon={<HelpOutlineOutlinedIcon fontSize="inherit" />}
            disabled={!canStartTour}
            onClick={startTour}
            id="editor-help"
            testId="editor-help"
          />

          <EditorRailButton
            label={t("exitEditor") as string}
            icon={<LogoutOutlinedIcon fontSize="inherit" />}
            danger
            onClick={onExitClicked}
            id="trainingeditor-exit-button"
            testId="trainingeditor-exit-button"
          />

          <EditorRailDivider testId="trainingeditor-360-account-divider" />

          <EditorRailButton
            label={accountTooltip ?? "account"}
            icon={<AccountCircleOutlinedIcon fontSize="inherit" />}
            active={Boolean(accountAnchorEl)}
            onClick={(event) =>
              setAccountAnchorEl(event.currentTarget as HTMLButtonElement)
            }
            testId="trainingeditor-360-account"
          />
        </>
      }
    >
      <EditorRailButton
        label={t("training_editor") as string}
        icon={<AccountTreeOutlinedIcon fontSize="inherit" />}
        active={selectedTab === SelectedTab.Timeline}
        onClick={() => dispatch(setSelectedTab(SelectedTab.Timeline))}
        id="trainingeditor-menu-button"
        testId="trainingeditor-menu-button"
      />
      <EditorRailButton
        label={t("save") as string}
        icon={<SaveOutlinedIcon fontSize="inherit" />}
        onClick={() => void onSaveProject()}
        disabled={!isDirty}
        id="trainingeditor-save-button"
        testId="trainingeditor-save-button"
      />
      <EditorRailButton
        label={t("publish") as string}
        icon={<SendOutlinedIcon fontSize="inherit" />}
        onClick={() => setIsPublishOpen(true)}
        id="trainingeditor-publish-button"
        testId="trainingeditor-publish-button"
        onboardingId={OnboardingTarget.Editor360PublishButton}
      />
      <EditorRailButton
        label={t("fileExplorer") as string}
        icon={<FolderOutlinedIcon fontSize="inherit" />}
        onClick={() => setIsFileExplorerOpen(true)}
        id="trainingeditor-file-explorer-button"
        testId="trainingeditor-file-explorer-button"
      />
      <EditorRailButton
        label={t("project_settings.title") as string}
        icon={<TuneOutlinedIcon fontSize="inherit" />}
        active={isProjectSettingsOpen}
        onClick={() => setIsProjectSettingsOpen(true)}
        id="trainingeditor-project-settings-button"
        testId="trainingeditor-project-settings-button"
      />

      <EditorRailDivider testId="trainingeditor-360-divider" />


      <EditorRailButton
        label={t("support") as string}
        icon={<LiveHelpOutlinedIcon fontSize="inherit" />}
        href="https://support.playit.training/play-it-creator"
        externalLink
        id="support"
        testId="support"
      />
      <EditorRailButton
        label={t("portal") as string}
        icon={<LanguageOutlinedIcon fontSize="inherit" />}
        href="https://portal.playit.training/"
        externalLink
        id="portal"
        testId="portal"
      />

      <FileExplorerDialog
        open={isFileExplorerOpen}
        onClose={() => setIsFileExplorerOpen(false)}
      />
      <ProjectSettingsDialog
        open={isProjectSettingsOpen}
        onClose={() => setIsProjectSettingsOpen(false)}
      />
      <PublishDialog open={isPublishOpen} onClose={() => setIsPublishOpen(false)} />
      <SettingsModal
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      <LocalChangesModal
        isOpen={isLocalChangesModalOpen}
        onClose={() => setIsLocalChangesModalOpen(false)}
        title="Local Changes"
        width="750px"
        height="calc(50vh / var(--app-zoom, 1))"
      />
      <PiConfirmationDialog
        open={confirmLeaveDialogOpen}
        onClose={() => setConfirmLeaveDialogOpen(false)}
        title={t("exit_editor.title")}
        text={t("exit_editor.description")}
        onConfirm={onExitEditor}
        confirmText={t("exit_editor.confirm")}
        cancelText={t("exit_editor.cancel")}
        confirmButtonVariant="piBlue"
      />
      <PiAccountDialog
        id="trainingeditor-360-account-popover"
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={() => setAccountAnchorEl(null)}
        switchOrgCallback={() => setIsSwitchOrganizationOpen(true)}
        settingsCallback={() => setIsSettingsOpen(true)}
      />
      <PiSwitchOrganizationDialog
        open={isSwitchOrganizationOpen}
        onConfirm={() => setIsSwitchOrganizationOpen(false)}
      />
    </EditorRail>
  );
}
